import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Save, X, Star } from "lucide-react";

const BLANK = {
  name: "",
  location: "",
  quote: "",
  rating: 5,
  date: "",
  display_order: 0,
  published: true,
};

export default function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {...item} | BLANK for new
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/testimonials/admin/all");
      setItems(data);
    } catch {
      toast.error("Failed to load testimonials");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ ...BLANK });
  const startEdit = (i) => setEditing({ ...i });
  const cancel = () => setEditing(null);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: editing.name,
        location: editing.location,
        quote: editing.quote,
        rating: Number(editing.rating),
        date: editing.date || undefined,
        display_order: Number(editing.display_order || 0),
        published: !!editing.published,
      };
      if (editing.id) {
        await api.patch(`/testimonials/${editing.id}`, payload);
        toast.success("Testimonial updated");
      } else {
        await api.post("/testimonials", payload);
        toast.success("Testimonial added");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await api.delete(`/testimonials/${id}`);
      setItems((arr) => arr.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex justify-between items-end flex-wrap gap-4 mb-6">
        <div>
          <div className="overline">Reviews</div>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Testimonials</h2>
          <p className="text-sm text-slate-500 mt-1">Curated reviews shown on the homepage.</p>
        </div>
        {!editing && (
          <button onClick={startNew} className="btn-accent text-sm" data-testid="admin-testimonial-new">
            <Plus size={14} /> New testimonial
          </button>
        )}
      </div>

      {editing && (
        <div className="bg-white border border-slate-200 p-6 mb-8" data-testid="admin-testimonial-form">
          <div className="overline mb-4">{editing.id ? "Edit testimonial" : "New testimonial"}</div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="t-name" />
            <Field label="Location / Project" value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} testid="t-location" />
            <Field label="Date (YYYY-MM-DD)" value={editing.date || ""} onChange={(v) => setEditing({ ...editing, date: v })} placeholder="2025-09-14" testid="t-date" />
            <div>
              <label className="overline">Rating</label>
              <select
                value={editing.rating}
                onChange={(e) => setEditing({ ...editing, rating: Number(e.target.value) })}
                className="field mt-2"
                data-testid="t-rating"
              >
                {[5,4,3,2,1].map((n) => <option key={n} value={n}>{n} stars</option>)}
              </select>
            </div>
            <Field
              label="Display order (lower = first)"
              value={editing.display_order}
              onChange={(v) => setEditing({ ...editing, display_order: v })}
              type="number"
              testid="t-order"
            />
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-slate-700 mb-2">
                <input
                  type="checkbox"
                  checked={!!editing.published}
                  onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                  data-testid="t-published"
                />
                Published
              </label>
            </div>
          </div>
          <div className="mt-4">
            <label className="overline">Quote</label>
            <textarea
              className="field mt-2"
              rows={4}
              value={editing.quote}
              onChange={(e) => setEditing({ ...editing, quote: e.target.value })}
              data-testid="t-quote"
            />
          </div>
          <div className="mt-5 flex gap-3">
            <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="admin-testimonial-save">
              <Save size={14} /> {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={cancel} className="btn-ghost text-sm" data-testid="admin-testimonial-cancel">
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200">
        <table className="w-full text-sm" data-testid="testimonials-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-left">
            <tr>
              <th className="p-4 overline">Reviewer</th>
              <th className="p-4 overline">Rating</th>
              <th className="p-4 overline">Order</th>
              <th className="p-4 overline">Status</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="p-8 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="p-12 text-center text-slate-500">No testimonials yet.</td></tr>
            )}
            {items.map((t) => (
              <tr key={t.id} className="border-b border-slate-200 hover:bg-slate-50 cursor-pointer" onClick={() => startEdit(t)} data-testid={`testimonial-row-${t.id}`}>
                <td className="p-4">
                  <div className="font-medium text-slate-900">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.location}</div>
                </td>
                <td className="p-4">
                  <div className="flex gap-0.5">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <Star key={i} size={14} className="text-[#C5A059] fill-[#C5A059]" strokeWidth={1.5} />
                    ))}
                  </div>
                </td>
                <td className="p-4 text-slate-600">{t.display_order}</td>
                <td className="p-4">
                  <span className={`px-2 py-1 text-[10px] font-medium tracking-wide border ${t.published ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-slate-100 border-slate-300 text-slate-700"}`}>
                    {t.published ? "PUBLISHED" : "DRAFT"}
                  </span>
                </td>
                <td className="p-4" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => remove(t.id)} aria-label="Delete" className="text-slate-400 hover:text-red-600" data-testid={`testimonial-delete-${t.id}`}>
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <label className="overline">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="field mt-2"
        placeholder={placeholder}
        data-testid={testid}
      />
    </div>
  );
}
